import { WorkflowGraphEdgeType } from "#src/batteries-included-components/Graph/WorkflowGraph/WorkflowGraphEdge/types";
import { WorkflowGraphNodeType } from "#src/batteries-included-components/Graph/WorkflowGraph/WorkflowGraphNode/types";
import { useListWorkflowTemplates } from "#src/components/hooks/adapters/useWorkflowTemplates";
import useLocalization from "#src/hooks/useLocalization";
import { useQuery } from "@tanstack/react-query";
import {
  FormSchemaAdapter,
  FormSchemaType,
  WorkflowTemplatesAdapter,
  WorkflowTemplateType,
} from "@validereinc/domain";
import { useReactFlow } from "@xyflow/react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { WorkflowGraphParallelNodeType } from "../ParallelGraphNode/types";
import {
  extractFormSchemaIdsFromWorkflowTemplate,
  extractWorkflowTemplateIdsFromWorkflowTemplate,
  getLayoutedElements,
  getPathMap,
  getReversePathMap,
  mapInitialVariables,
  mapTaskVariables,
  mapTemplateVariables,
  mapTriggerVariables,
  workflowTemplateToEdges,
  workflowTemplateToNodes,
} from "./useWorkflowGraph.helpers";
import { WorkflowVisualizationProp } from "./WorkflowGraphCanvas";
import { useWorkflowGraphState } from "./WorkflowGraphStore";

const useWorkflowGraph = (props: WorkflowVisualizationProp) => {
  const { fitView } = useReactFlow();

  const {
    nodes,
    edges,
    setNodes,
    setEdges,
    setOriginalConfiguration,
    setIsFetchingConfiguration,
    setOriginalTriggers,
    setTemplateVariables,
    setTaskVariables,
    setTriggerVariables,
    setInitialVariables,
    setPathMap,
    setReversePathMap,
    currentConfiguration,
    currentTriggers,
    isFetchingConfiguration,
    setCurrentConfiguration,
    setCurrentTriggers,
    isInEditMode,
  } = useWorkflowGraphState();

  const { localize } = useLocalization();

  const { templateId } = props;

  useEffect(() => {
    if (!templateId) return;
    setIsFetchingConfiguration(true);
    WorkflowTemplatesAdapter.getOne({ id: templateId })
      .then((data) => data.data)
      .then((values) => {
        const pathMap = getPathMap(values.config);
        setOriginalConfiguration(values.config);
        setOriginalTriggers(values.triggers);
        setCurrentConfiguration(values.config);
        setCurrentTriggers(values.triggers);
        setTemplateVariables(mapTemplateVariables(values));
        setTaskVariables(mapTaskVariables(values.config));
        setTriggerVariables(mapTriggerVariables(values.triggers));
        setInitialVariables(mapInitialVariables(values));
        setPathMap(pathMap);
        setReversePathMap(getReversePathMap(pathMap));
      })
      .finally(() => {
        setIsFetchingConfiguration(false);
      });
  }, [templateId]);

  const formSchemaIdsToFetch = useMemo(
    () =>
      extractFormSchemaIdsFromWorkflowTemplate({
        config: currentConfiguration,
        triggers: currentTriggers,
      }),
    [currentConfiguration, currentTriggers]
  );

  const formSchemasQuery = useQuery({
    queryKey: ["formSchema", { $in: formSchemaIdsToFetch }],
    queryFn: async () =>
      FormSchemaAdapter.getList({
        filters: {
          id: formSchemaIdsToFetch,
        },
        pageSize: 500,
      }),
    enabled: formSchemaIdsToFetch.length > 0,
  });

  const sortedFormSchemasById = useMemo(() => {
    const sorted: Record<string, FormSchemaType> = {};

    (formSchemasQuery.data?.data ?? []).forEach((formSchema) => {
      sorted[formSchema.id] = formSchema;
    });

    return sorted;
  }, [formSchemasQuery.data?.data]);

  const workflowTemplateIdsToFetch = useMemo(
    () =>
      extractWorkflowTemplateIdsFromWorkflowTemplate({
        triggers: currentTriggers,
      }),
    [currentTriggers]
  );

  const workflowTemplatesQuery = useListWorkflowTemplates(
    {
      filters: {
        id: workflowTemplateIdsToFetch,
      },
    },
    { enabled: workflowTemplateIdsToFetch.length > 0 }
  );

  const sortedWorkflowTemplatesById = useMemo(() => {
    const sorted: Record<string, WorkflowTemplateType> = {};

    (workflowTemplatesQuery.data?.data ?? []).forEach((workflowTemplate) => {
      sorted[workflowTemplate.id] = workflowTemplate;
    });

    return sorted;
  }, [workflowTemplatesQuery.data?.data]);

  const isLoading =
    (formSchemaIdsToFetch.length > 0 && formSchemasQuery.isFetching) ||
    (workflowTemplateIdsToFetch.length > 0 &&
      workflowTemplatesQuery.isFetching) ||
    (!!templateId && isFetchingConfiguration);

  const generatedNodesAndEdges = useMemo(() => {
    if (isLoading) return { finalNodes: [], finalEdges: [] };

    const finalNodes = workflowTemplateToNodes({
      template: {
        config: currentConfiguration,
        triggers: currentTriggers,
      },
      formSchemas: sortedFormSchemasById,
      workflowTemplates: sortedWorkflowTemplatesById,
      localize,
    });
    const finalEdges = workflowTemplateToEdges(
      {
        config: currentConfiguration,
        triggers: currentTriggers,
      },
      {
        formSchemas: sortedFormSchemasById,
      }
    );

    return { finalNodes, finalEdges };
  }, [
    sortedFormSchemasById,
    sortedWorkflowTemplatesById,
    currentConfiguration,
    currentTriggers,
    isLoading,
    isInEditMode,
  ]);

  const initialSetupVariables = useRef({
    initialNodesAndEdgesSet: false,
    initialFitViewRan: false,
  });

  const autoLayoutAndUpdateState = useCallback(
    async ({
      nodes,
      edges,
    }: {
      nodes: Array<WorkflowGraphNodeType | WorkflowGraphParallelNodeType>;
      edges: WorkflowGraphEdgeType[];
    }) => {
      const { nodes: layoutedNodes, edges: layoutedEdges } =
        await getLayoutedElements(nodes, edges);
      setNodes(layoutedNodes);
      setEdges(layoutedEdges);
    },
    [setNodes, setEdges]
  );

  useEffect(() => {
    const currentNodePositions = new Map(
      nodes.map((node) => [node.id, node.position])
    );

    // Merge generated nodes with current positions if they exist in the store
    // This ensures manually moved positions are preserved when structure changes
    const nodesWithPreservedPositions = generatedNodesAndEdges.finalNodes.map(
      (node) => {
        const currentPosition = currentNodePositions.get(node.id);
        if (currentPosition) {
          return {
            ...node,
            position: currentPosition,
          };
        }
        return node;
      }
    );

    autoLayoutAndUpdateState({
      nodes: nodesWithPreservedPositions,
      edges: generatedNodesAndEdges.finalEdges,
    }).then(() => {
      initialSetupVariables.current.initialNodesAndEdgesSet = true;
    });
  }, [JSON.stringify(generatedNodesAndEdges), autoLayoutAndUpdateState]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    if (
      initialSetupVariables.current.initialNodesAndEdgesSet &&
      !initialSetupVariables.current.initialFitViewRan
    ) {
      timeout = setTimeout(() => {
        window.requestAnimationFrame(() => {
          fitView();
          initialSetupVariables.current.initialFitViewRan = true;
        });
      }, 100);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [JSON.stringify({ nodes, edges })]);

  return {
    autoLayoutAndUpdateState,
    isLoading,
  };
};

export { useWorkflowGraph };
